/**
 * The shop grid's behavior half (studio-shop D4): the catalog's products in the
 * studio's order, each with its lowest price per format and its whole range,
 * and Open, which shows a product in the page's product window with this grid's
 * order for its previous and next. Draws nothing. Its pictures are Telify's small copies (shop-picture-copies), the
 * service's own until a copy is made; the product window keeps the service's.
 * shop-self-fulfilment: `ships` keeps only one kind, so Open steps through those.
 */
import { useMemo } from 'react';

import { shopPrice, useShopCatalog } from '../../api/shop';
import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useShopPage } from '../../hooks/useShopPage';
import { contract, type ShopGridActions, type ShopGridEvents, type ShopGridProduct, type ShopGridState } from './contract';

export type ShopGridRuntime = PieceRuntime<typeof contract, ShopGridState, ShopGridActions>;

const label = (format: string | null) => (format ? format.charAt(0).toUpperCase() + format.slice(1) : null);

export function useShopGrid(input: PieceInput<ShopGridEvents> = {}): ShopGridRuntime {
  const options = usePieceOptions(contract, input.options);
  const catalog = useShopCatalog();
  const { page } = useShopPage();
  const { ships } = options;

  const products = useMemo<ShopGridProduct[]>(() => {
    const data = catalog.data;
    if (!data) return [];
    const money = (amount: number) => shopPrice(amount.toFixed(2), data.currency);
    return data.products.filter((p) => ships === 'all' || p.shippedBy === ships).map((p) => {
      const lowest = new Map<string | null, number>();
      for (const o of p.options) lowest.set(o.format, Math.min(lowest.get(o.format) ?? Infinity, Number(o.price)));
      const prices = [...lowest].map(([format, price]) => {
        const name = label(format);
        return name ? `${name} from ${money(price)}` : `From ${money(price)}`;
      });
      const all = p.options.map((o) => Number(o.price));
      const image = p.images[0];
      return {
        handle: p.handle,
        title: p.title,
        image: image ? { url: image.small ?? image.url, alt: image.alt ?? p.title } : null,
        prices: prices.join(' · '),
        from: money(Math.min(...all)),
        to: money(Math.max(...all)),
        options: p.options.map((o) => ({
          optionId: o.id,
          format: o.format,
          size: o.size,
          label: o.label,
          price: shopPrice(o.price, data.currency),
          image: o.image ? { url: o.image.small ?? o.image.url, alt: o.image.alt ?? `${p.title}, ${o.label}` } : null,
        })),
        soldOut: p.options.every((o) => o.soldOut),
        shippedBy: p.shippedBy,
      };
    });
  }, [catalog.data, ships]);

  const actions = useMemo<ShopGridActions>(
    () => ({
      open: (handle) => {
        page.warnNoWindow();
        page.openProduct(
          handle,
          products.map((p) => p.handle),
        );
      },
    }),
    [page, products],
  );

  const status: ShopGridState['status'] = catalog.data ? (products.length ? 'ready' : 'empty') : 'loading';
  // The same state until the catalog changes, so a site's drawing is not redrawn under its own motion.
  const state = useMemo<ShopGridState>(() => ({ status, products }), [status, products]);
  return { state, actions, options };
}
